/* eslint-disable @typescript-eslint/no-explicit-any */
import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import type {
	MaybeDrafted,
	PatchCollection,
	UpdateQueryDataThunk,
} from '@reduxjs/toolkit/dist/query/core/buildThunks';
import type {
	EndpointDefinitions,
	QueryKeys,
	ResultTypeFrom,
	TagDescription,
} from '@reduxjs/toolkit/query';
import type { RootState } from '@reduxjs/toolkit/src/query';

interface Util<
	Definitions extends EndpointDefinitions,
	ReducerPath extends string,
	TagTypes extends string
> {
	updateQueryData: UpdateQueryDataThunk<
		Definitions,
		RootState<Definitions, string, ReducerPath>
	>;
	selectInvalidatedBy: (
		state: RootState<Definitions, string, ReducerPath>,
		tags: ReadonlyArray<TagDescription<TagTypes>>
	) => {
		endpointName: string;
		originalArgs: any;
		queryCacheKey: string;
	}[];
}

type PickFn<T> = (draft: MaybeDrafted<T>) => void;

type UpdateFn<T, A extends Record<string, unknown> = {}> = (
	draft: MaybeDrafted<T>,
	args: Partial<A> | null | undefined
) => void;

type Picker<S, T> = (draft: MaybeDrafted<S>, update: PickFn<T>) => void;

type Updater<T, A extends Record<string, unknown> = {}> = (
	getState: () => any,
	dispatch: ThunkDispatch<any, any, AnyAction>,
	update: UpdateFn<T, A>
) => PatchCollection[];

/**
 * `Entity` class representing an entity in the RTK Query caches that can be updated from multiple sources at once.
 *
 * @example
 *
 * const userEntity = new Entity<User>();
 *
 * userEntity.addSingleSource(userApi.util, 'getUserById', [ApiTags.User], (response, pick) => {
 *   pick(response.user);
 * });
 * userEntity.addCollectionSource(userApi.util, 'getUsers', [ApiTags.User], (response, pick) => {
 *  pick(response.users);
 * });
 *
 * // Update user entity with id 123
 * userEntity.update(getState, dispatch, (user) => {
 *	 if (user.id === 123) {
 *	   user.name = 'New Name';
 *	 }
 * });
 *
 * // Remove user entity with id 123 from all collections
 * userEntity.updateCollection(getState, dispatch, (users) => {
 *	 const removeIndex = users.findIndex(user => user.id === 123);
 *
 *   if (removeIndex !== -1) {
 *	   users.splice(removeIndex, 1);
 *	 }
 * });
 */
export class Entity<T> {
	#singleupdaters: Updater<T>[] = [];
	#collectionUpdaters: Updater<Readonly<T>[]>[] = [];

	/**
	 * Method to add a source for single entity updates.
	 *
	 * ! If entity is part of collection, please use `addCollectionSource` method !
	 *
	 * @example
	 *
	 * const userEntity = new Entity<User>();
	 *
	 * userEntity.addSingleSource(userApi.util, 'getUserById', [ApiTags.User], (response, pick) => {
	 *   pick(response.user);
	 * });
	 *
	 * @param util - RTK Query util object
	 * @param endpointName - Name of the endpoint to update
	 * @param tags - Tags that invalidate this entity
	 * @param picker - Function to pick the entity from the source cache object
	 */
	addSingleSource<
		Definitions extends EndpointDefinitions,
		ReducerPath extends string,
		EndpointName extends QueryKeys<Definitions>,
		TagTypes extends string
	>(
		util: Util<Definitions, ReducerPath, TagTypes>,
		endpointName: EndpointName,
		tags: ReadonlyArray<TagDescription<TagTypes>>,
		picker: Picker<ResultTypeFrom<Definitions[EndpointName]>, T>
	) {
		const updater: Updater<T> = (getState, dispatch, update) => {
			const patches = [];

			for (const { originalArgs } of util.selectInvalidatedBy(
				getState(),
				tags
			)) {
				patches.push(
					dispatch(
						util.updateQueryData(endpointName, originalArgs, (draft) => {
							picker(draft, (entity) => update(entity, originalArgs));
						})
					)
				);
			}

			return patches;
		};

		this.#singleupdaters.push(updater);
	}

	/**
	 * Method to add a source for collection entity updates.
	 *
	 * Each item in the collection will be also added as a single entity source.
	 *
	 * @example
	 *
	 * const userEntity = new Entity<User>();
	 *
	 * userEntity.addCollectionSource(userApi.util, 'getUsers', [ApiTags.User], (response, pick) => {
	 *   pick(response.users);
	 * });
	 *
	 * @param util - RTK Query util object
	 * @param endpointName - Name of the endpoint to update
	 * @param tags - Tags that invalidate this entity
	 * @param picker - Function to pick the entities from the source cache object
	 */
	addCollectionSource<
		Definitions extends EndpointDefinitions,
		ReducerPath extends string,
		EndpointName extends QueryKeys<Definitions>,
		TagTypes extends string
	>(
		util: Util<Definitions, ReducerPath, TagTypes>,
		endpointName: EndpointName,
		tags: ReadonlyArray<TagDescription<TagTypes>>,
		picker: Picker<ResultTypeFrom<Definitions[EndpointName]>, T[]>
	) {
		this.addSingleSource(util, endpointName, tags, (draft, singlePick) => {
			picker(draft, (collection) => {
				collection.forEach(singlePick);
			});
		});

		const updater: Updater<T[]> = (getState, dispatch, update) => {
			const patches = [];
			const updatedCacheKeys = new Set<string>();

			for (const cacheId of util.selectInvalidatedBy(getState(), tags)) {
				if (
					endpointName !== cacheId.endpointName ||
					updatedCacheKeys.has(cacheId.queryCacheKey)
				) {
					continue;
				}

				updatedCacheKeys.add(cacheId.queryCacheKey);

				patches.push(
					dispatch(
						util.updateQueryData(
							endpointName,
							cacheId.originalArgs,
							(draft) => {
								picker(draft, (entities) =>
									update(entities, cacheId.originalArgs)
								);
							}
						)
					)
				);
			}

			return patches;
		};

		this.#collectionUpdaters.push(updater);
	}

	/**
	 * Method to update entity from all registered sources with provided update function.
	 *
	 * @example
	 *
	 * const userEntity = new Entity<User>();
	 *
	 * userEntity.addSingleSource(userApi.util, 'getUserById', [ApiTags.User], (response, pick) => {
	 *   pick(response.user);
	 * });
	 * userEntity.addCollectionSource(userApi.util, 'getUsers', [ApiTags.User], (response, pick) => {
	 *   pick(response.users);
	 * });
	 *
	 * // Update user entity with id 123
	 * userEntity.update(getState, dispatch, (user) => {
	 *	 if (user.id === 123) {
	 *	   user.name = 'New Name';
	 *	 }
	 * });
	 *
	 * @param getState - Get current redux state
	 * @param dispatch - Redux dispatch function
	 * @param update - Update function to apply on the each entity
	 * @returns Array of applied patches
	 */
	update<A extends Record<string, unknown> = {}>(
		getState: () => any,
		dispatch: ThunkDispatch<any, any, AnyAction>,
		update: UpdateFn<T, A>
	) {
		const allPatches = [];

		for (const updater of this.#singleupdaters) {
			allPatches.push(...updater(getState, dispatch, update));
		}

		return allPatches;
	}

	/**
	 * Method to update collections of entities from all registered sources with provided update function.
	 *
	 * ! Here you can update only collection: add/remove/move items. Entities updates should be done via `update` method !
	 *
	 * @example
	 *
	 * const userEntity = new Entity<User>();
	 *
	 * userEntity.addCollectionSource(userApi.util, 'getUsers', [ApiTags.User], (response, pick) => {
	 *   pick(response.users);
	 * });
	 *
	 * // Remove user entity with id 123 from all collections
	 * userEntity.updateCollection(getState, dispatch, (users) => {
	 *	 const removeIndex = users.findIndex(user => user.id === 123);
	 *
	 *   if (removeIndex !== -1) {
	 *	   users.splice(removeIndex, 1);
	 *	 }
	 * });
	 *
	 * @param getState - Get current redux state
	 * @param dispatch - Redux dispatch function
	 * @param update - Update function to apply on the each entity collection
	 * @returns Array of applied patches
	 */
	updateCollection<A extends Record<string, unknown> = {}>(
		getState: () => any,
		dispatch: ThunkDispatch<any, any, AnyAction>,
		update: UpdateFn<Readonly<T>[], A>
	) {
		const allPatches = [];

		for (const updater of this.#collectionUpdaters) {
			allPatches.push(...updater(getState, dispatch, update));
		}

		return allPatches;
	}
}
